import { batch, Signal, signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import {
  filter,
  find,
  findIndex,
  findLastIndex,
  first,
  forEach,
  includes,
  isEmpty,
  map,
  max,
  min,
  orderBy,
  reduce,
  some,
  times,
} from 'es-toolkit/compat';
import {
  getViewerIndexedUrlSetting,
  hasExplicitMultiViewerSettings,
  shouldAllowViewerUrlStateFallback,
  useViewerLayoutUrlState,
} from 'components/Procedure/viewerStateUrlFallback';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';

import { QueryFunctionContext } from '@tanstack/react-query';
import {
  getNormalizationValues,
  getNormalizationValuesQueryKey,
  getSlideChannelNormalizations,
  getSlideChannelNormalizationsQueryKey,
} from 'api/slideMultiplexChannelNormalizations';
import { MAX_VIEWERS } from 'components/Procedure/SlidesViewer/constants';
import { SlideWithChannelAndResults } from 'components/Procedure/useSlideChannelsAndResults/utils';
import { Accession } from 'interfaces/accession';
import { NormalizationResult } from 'interfaces/experimentResults';
import { Procedure } from 'interfaces/procedure';
import { Slide, SlideChannel } from 'interfaces/slide';
import { useQueryParam } from 'use-query-params';
import { MAX_UINT16, MAX_UINT8 } from 'utils/constants';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useSelectedSlideIds } from 'utils/useCurrentSlideIds';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import usePrevious from 'utils/usePrevious';
import {
  useQueriesWithErrorAndRetrySnackbar,
  useQueryWithErrorAndRetrySnackbar,
} from 'utils/useQueryWithErrorAndRetrySnackbar';
import { shouldLoadMultiplexNormalizations } from './normalizationEligibility';

export type NormalizationRange = {
  range: [min: number, max: number];
  dirty?: boolean; // Indicates if the range has been manually adjusted
};

// { [slideId]: { [channelId]: Signal<NormalizationRange> } }
export const slidesChannelNormalizationSettings: Array<
  Signal<Record<string, Record<string, Signal<NormalizationRange>>>>
> = times(MAX_VIEWERS, () => signal({}));
export const slidesChannelNormalizationLoadingStates: Array<Signal<Record<string, boolean>>> = times(MAX_VIEWERS, () =>
  signal({})
);

export const getSlideChannelNormalizationLoadingState = (viewerIndex: number, slideId: string): boolean =>
  Boolean(slidesChannelNormalizationLoadingStates[viewerIndex]?.value[slideId]);

const useSyncSlideChannelNormalizationLoadingState = (
  slide: Pick<SlideWithChannelAndResults, 'id' | 'viewerIndex'>,
  isLoading: boolean
): boolean => {
  const viewerLoadingStates = slidesChannelNormalizationLoadingStates[slide.viewerIndex];

  useEffect(() => {
    if (viewerLoadingStates?.value[slide.id] !== isLoading) {
      viewerLoadingStates.value = {
        ...viewerLoadingStates.value,
        [slide.id]: isLoading,
      };
    }
  }, [isLoading, slide.id, viewerLoadingStates]);

  return getSlideChannelNormalizationLoadingState(slide.viewerIndex, slide.id) || isLoading;
};

export const clearChannelNormalizationSettingsSignals = () => {
  forEach(slidesChannelNormalizationSettings, (viewerSettings, viewerIndex) => {
    viewerSettings.value = {};
    slidesChannelNormalizationLoadingStates[viewerIndex].value = {};
  });
};

const getMostRecentSlideSettingsForViewer = <T,>(
  settingsBySlide: Record<string, T> | undefined,
  currentSlideId?: string
): T | undefined => {
  const previousSlideSettings = Object.entries(settingsBySlide || {}).filter(([slideId]) => slideId !== currentSlideId);
  return previousSlideSettings.length ? previousSlideSettings[previousSlideSettings.length - 1][1] : undefined;
};

export const useLoadSelectMultiplexSlideNormalizationOptions = ({
  procedure,
}: {
  procedure: Procedure | Accession;
}) => {
  const { queryParams } = useEncodedFilters();
  const studyId = procedure?.studyId || queryParams?.filters?.studyId;
  const slides = procedure?.slides || [];
  const [selectedSlideIds] = useSelectedSlideIds(procedure);
  const multiplexSlides = filter(
    slides,
    (slide) =>
      includes(selectedSlideIds, slide.id) &&
      shouldLoadMultiplexNormalizations(slide) &&
      !isEmpty(slide.channels) &&
      Boolean(slide.id) &&
      Boolean(studyId)
  );

  const queries = useQueriesWithErrorAndRetrySnackbar({
    queries: map(multiplexSlides, (slide) => ({
      queriedEntityName: `Channel Normalizations for slide ${slide.id}`,
      consoleOnly: true,
      queryKey: getSlideChannelNormalizationsQueryKey({ id: slide.id, labId: slide.labId, studyId }),
      queryFn: ({ signal: abortSignal }: QueryFunctionContext) =>
        getSlideChannelNormalizations({ id: slide.id, labId: slide.labId, studyId }, abortSignal),
    })),
  });

  return {
    isLoading: some(queries, ({ isLoading }) => isLoading),
  };
};

export const useMultiplexSlideChannelNormalizationsOptions = ({ slide }: { slide: Slide & { studyId?: string } }) => {
  const { labId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const studyId = slide?.studyId || queryParams?.filters?.studyId;
  const enabled = shouldLoadMultiplexNormalizations(slide) && Boolean(studyId);
  const {
    data: unsortedNormalizationOptions,
    isLoading: isLoadingNormalizationOptions,
    refetch,
  } = useQueryWithErrorAndRetrySnackbar(
    getSlideChannelNormalizationsQueryKey({ id: slide.id, labId: slide.labId ?? labId, studyId }),
    ({ signal: abortSignal }) =>
      getSlideChannelNormalizations({ id: slide.id, labId: slide.labId ?? labId, studyId }, abortSignal),
    { queriedEntityName: 'normalizations', enabled, retry: false }
  );

  const normalizationOptions = useMemo(
    () =>
      orderBy(
        unsortedNormalizationOptions,
        [(normalization) => normalization.studyId === studyId, 'createdAt'],
        ['desc', 'desc']
      ),
    [unsortedNormalizationOptions, studyId]
  );

  const defaultNormalization =
    find(normalizationOptions, { approved: true }) || // Use the first approved normalization
    find(normalizationOptions, { internallyApproved: true }) || // Use the first internally approved normalization
    first(normalizationOptions); // Use the most recent normalization
  return {
    normalizationOptions,
    defaultNormalization,
    isLoadingNormalizationOptions: enabled && isLoadingNormalizationOptions,
    refetchNormalizations: refetch,
  };
};

export const computeDynamicRangeFromNormalizationOrChannelHistogram = (
  channelIndex: number,
  normalizationData?: NormalizationResult,
  is16Bit = false,
  channelHistogram?: Pick<SlideChannel, 'histogram'>['histogram']
): [min: number, max: number] => {
  const channelNormalizationResult = find(
    normalizationData?.channel_normalization_results,
    (c) => `${c.channel_index}` === `${channelIndex}`
  );

  const maxChannelRange = is16Bit ? MAX_UINT16 : MAX_UINT8;
  const minColorRange =
    channelNormalizationResult?.params?.min_c ??
    ((channelHistogram ? findIndex(channelHistogram, (value) => value > 0) : 0) / MAX_UINT8) * maxChannelRange;
  const maxColorRange =
    channelNormalizationResult?.params?.max_c ??
    ((channelHistogram ? findLastIndex(channelHistogram, (value) => value > 0) : MAX_UINT8) / MAX_UINT8) *
      maxChannelRange;

  return [max([minColorRange, 0]), min([max([maxColorRange, minColorRange + 1]), maxChannelRange])];
};

export const useMultiplexSlideChannelNormalizationData = (
  slide: Slide,
  experimentResultId: number,
  enabled: boolean = true
) => {
  // The backend will return an empty object if there is no normalization data - so we will check the loaded data before using it
  const {
    data: loadedNormalizationData,
    isLoading,
    refetch: refetchNormalization,
  } = useQueryWithErrorAndRetrySnackbar(
    getNormalizationValuesQueryKey(slide.id, experimentResultId),
    ({ signal: abortSignal }) => getNormalizationValues<NormalizationResult>(slide.id, experimentResultId, abortSignal),
    {
      enabled,
      queriedEntityName: 'multiplex channel normalization data',
      retry: false,
    }
  );
  // Only use the normalization data if it is not empty (i.e. the backend returned data). This will allow us to check for loading errors
  // Normalization results should return an object with a key 'channel_normalization_results' which contains the normalization data per channel
  const normalizationData = !isEmpty(loadedNormalizationData?.channel_normalization_results)
    ? loadedNormalizationData
    : undefined;

  return {
    normalizationData,
    isLoadingNormalizationDataById: enabled && isLoading,
    refetchNormalization,
  };
};

const useApplySlideChannelNormalizationSettings = ({
  slide,
  currentNormalizationId,
  normalizationData,
  isLoadingNormalizationData,
  urlMarkerNormalizationValues,
  shouldAllowViewerUrlFallback,
  defaultFromChannelHistogram,
  refreshExistingChannelSettings,
}: {
  slide: SlideWithChannelAndResults;
  currentNormalizationId: number | null;
  normalizationData?: NormalizationResult;
  isLoadingNormalizationData: boolean;
  urlMarkerNormalizationValues?: {
    [markerType: string]: { [viewerIndex: number]: NormalizationRange };
  };
  shouldAllowViewerUrlFallback: boolean;
  defaultFromChannelHistogram?: boolean;
  refreshExistingChannelSettings: boolean;
}) => {
  const slideId = slide.id;
  const viewerSlidesChannelNormalizationSettings = slidesChannelNormalizationSettings[slide.viewerIndex];
  const previousCurrentNormalizationId = usePrevious(currentNormalizationId);
  const previousNormalizationData = usePrevious(normalizationData);
  const previousSlideId = usePrevious(slide?.id);

  useEffect(() => {
    if (isLoadingNormalizationData) {
      return;
    }
    if (!viewerSlidesChannelNormalizationSettings) {
      console.warn(`Invalid viewerIndex: ${slide.viewerIndex}`);
      return;
    }

    batch(() => {
      const previousViewerSlidesChannelsNormalizationSettings = viewerSlidesChannelNormalizationSettings.value;
      const previousSlideChannelNormalizationSettings =
        previousViewerSlidesChannelsNormalizationSettings[slideId] || {};
      const previousViewerSlideSettingsForFallback = getMostRecentSlideSettingsForViewer(
        previousViewerSlidesChannelsNormalizationSettings,
        slideId
      );

      const newSlideChannelSettings: Record<string, Signal<NormalizationRange>> = reduce(
        Object.entries(slide.channels || {}),
        (acc, [channelIndex, channel]) => {
          const existingChannelSettings = acc[channelIndex]?.value;
          const hasPreviousNormalizationSelection = previousCurrentNormalizationId !== undefined;
          const shouldRefreshExistingChannelSettings =
            refreshExistingChannelSettings &&
            hasPreviousNormalizationSelection &&
            previousSlideId === slideId &&
            (previousCurrentNormalizationId !== currentNormalizationId ||
              (previousNormalizationData !== normalizationData && !existingChannelSettings?.dirty));

          if (existingChannelSettings && !shouldRefreshExistingChannelSettings) {
            return acc;
          }

          const markerType = slide?.channelMarkerTypes?.[channelIndex];
          const explicitUrlMarkerNormalizationValue = markerType
            ? urlMarkerNormalizationValues?.[markerType]?.[slide.viewerIndex]
            : undefined;
          const fallbackUrlMarkerNormalizationValue =
            markerType && !explicitUrlMarkerNormalizationValue
              ? getViewerIndexedUrlSetting({
                  settingsByViewer: urlMarkerNormalizationValues?.[markerType],
                  viewerIndex: slide.viewerIndex,
                  shouldAllowFallback: shouldAllowViewerUrlFallback,
                })
              : undefined;
          const previousViewerChannelSettings = previousSlideChannelNormalizationSettings[channelIndex]
            ? undefined
            : previousViewerSlideSettingsForFallback?.[channelIndex]?.value;
          const preservedViewerNormalizationValue = previousViewerChannelSettings?.dirty
            ? previousViewerChannelSettings
            : undefined;
          const resolvedUrlNormalizationValue =
            explicitUrlMarkerNormalizationValue ?? fallbackUrlMarkerNormalizationValue;
          const range =
            resolvedUrlNormalizationValue?.range ||
            preservedViewerNormalizationValue?.range ||
            computeDynamicRangeFromNormalizationOrChannelHistogram(
              Number(channelIndex),
              normalizationData,
              slide.encoding === 'uint16',
              defaultFromChannelHistogram ? channel?.histogram : undefined
            );
          const nextNormalizationSetting = preservedViewerNormalizationValue ? { range, dirty: true } : { range };

          if (!acc[channelIndex]) {
            acc[channelIndex] = signal(nextNormalizationSetting);
          } else if (shouldRefreshExistingChannelSettings) {
            // Preserve explicit per-slide settings when revisiting a slide, but refresh the current slide when
            // a new normalization selection or fresh normalization data should replace the derived defaults.
            acc[channelIndex].value = nextNormalizationSetting;
          }
          return acc;
        },
        previousSlideChannelNormalizationSettings
      );

      viewerSlidesChannelNormalizationSettings.value = {
        ...(previousViewerSlidesChannelsNormalizationSettings || {}),
        [slideId]: newSlideChannelSettings,
      };
    });
  }, [
    currentNormalizationId,
    viewerSlidesChannelNormalizationSettings,
    slideId,
    slide,
    normalizationData,
    isLoadingNormalizationData,
    urlMarkerNormalizationValues,
    shouldAllowViewerUrlFallback,
    defaultFromChannelHistogram,
    refreshExistingChannelSettings,
  ]);
};

export const useInitializeDefaultSlideChannelNormalizationSettings = ({
  slide,
  defaultFromChannelHistogram,
}: {
  slide: SlideWithChannelAndResults;
  defaultFromChannelHistogram?: boolean;
}) => {
  useSignals();
  const { selectedSlideIds, viewersStainPreferences } = useViewerLayoutUrlState();

  const [urlMarkerNormalizationValues] = useQueryParam<{
    [markerType: string]: { [viewerIndex: number]: NormalizationRange };
  }>('markerNormalizationValues', CompressedJsonParam);
  const shouldAllowViewerUrlFallback = shouldAllowViewerUrlStateFallback({
    hasExplicitMultiViewerState: hasExplicitMultiViewerSettings(urlMarkerNormalizationValues),
    selectedSlideIds,
    viewersStainPreferences,
  });
  const { defaultNormalization, isLoadingNormalizationOptions } = useMultiplexSlideChannelNormalizationsOptions({
    slide,
  });

  const defaultNormalizationId = defaultNormalization?.experimentResultId ?? null;
  const canLoadNormalizationData = defaultNormalizationId !== null;
  const { normalizationData, isLoadingNormalizationDataById } = useMultiplexSlideChannelNormalizationData(
    slide,
    defaultNormalizationId ?? -1,
    canLoadNormalizationData
  );

  const isLoadingNormalizationData =
    isLoadingNormalizationOptions || (canLoadNormalizationData && isLoadingNormalizationDataById);

  useApplySlideChannelNormalizationSettings({
    slide,
    currentNormalizationId: defaultNormalizationId,
    normalizationData,
    isLoadingNormalizationData,
    urlMarkerNormalizationValues,
    shouldAllowViewerUrlFallback,
    defaultFromChannelHistogram,
    refreshExistingChannelSettings: false,
  });
  useSyncSlideChannelNormalizationLoadingState(slide, isLoadingNormalizationData);
};

export const useMultiplexSlideChannelNormalization = ({
  slide,
  defaultFromChannelHistogram,
}: {
  slide: SlideWithChannelAndResults;
  defaultFromChannelHistogram?: boolean;
}) => {
  useSignals();
  const [, startTransition] = useTransition();
  const { selectedSlideIds, viewersStainPreferences } = useViewerLayoutUrlState();
  const [urlMarkerNormalizationValues, setUrlMarkerNormalizationValues] = useQueryParam<{
    [markerType: string]: { [viewerIndex: number]: NormalizationRange };
  }>('markerNormalizationValues', CompressedJsonParam);
  const shouldAllowViewerUrlFallback = shouldAllowViewerUrlStateFallback({
    hasExplicitMultiViewerState: hasExplicitMultiViewerSettings(urlMarkerNormalizationValues),
    selectedSlideIds,
    viewersStainPreferences,
  });
  const { defaultNormalization, normalizationOptions, isLoadingNormalizationOptions } =
    useMultiplexSlideChannelNormalizationsOptions({
      slide,
    });

  const [manualNormalizationId, setCurrentNormalizationId] = useState(defaultNormalization?.experimentResultId ?? null);
  const currentNormalizationId = manualNormalizationId ?? defaultNormalization?.experimentResultId ?? null;
  const currentNormalization = find(
    normalizationOptions,
    (normalization) => normalization.experimentResultId === currentNormalizationId
  );

  const slideId = slide.id;
  const viewerSlidesChannelNormalizationSettings = slidesChannelNormalizationSettings[slide.viewerIndex];

  const canLoadNormalizationData = currentNormalization?.experimentResultId !== undefined;
  const { normalizationData, isLoadingNormalizationDataById, refetchNormalization } =
    useMultiplexSlideChannelNormalizationData(
      slide,
      currentNormalization?.experimentResultId ?? -1,
      canLoadNormalizationData
    );

  const isLoadingNormalizationData =
    isLoadingNormalizationOptions || (canLoadNormalizationData && isLoadingNormalizationDataById);

  const resetChannelNormalizationSettings = useCallback(
    (channelIndex: number, markerType?: string) => {
      if (!viewerSlidesChannelNormalizationSettings) {
        console.warn(`Invalid viewerIndex: ${slide.viewerIndex}`);
        return;
      }
      const previousViewerSlidesChannelsNormalizationSettings = viewerSlidesChannelNormalizationSettings.value;
      const slideChannelNormalizationSettings = previousViewerSlidesChannelsNormalizationSettings[slideId] || {};

      const channel = slide.channels?.[channelIndex];
      const range = computeDynamicRangeFromNormalizationOrChannelHistogram(
        Number(channelIndex),
        normalizationData,
        slide.encoding === 'uint16',
        defaultFromChannelHistogram ? channel?.histogram : undefined
      );

      if (!slideChannelNormalizationSettings[channelIndex]) {
        slideChannelNormalizationSettings[channelIndex] = signal({ range });
      } else {
        // Update the range if the slide has changed or if new normalization data is available
        slideChannelNormalizationSettings[channelIndex].value = { range };
      }
      viewerSlidesChannelNormalizationSettings.value = {
        ...(previousViewerSlidesChannelsNormalizationSettings || {}),
        [slideId]: slideChannelNormalizationSettings,
      };

      if (markerType && urlMarkerNormalizationValues) {
        startTransition(() => {
          setUrlMarkerNormalizationValues((prev) => {
            if (!prev?.[markerType]) {
              return prev;
            }
            const newMarkerNormalizationValues = prev;
            delete newMarkerNormalizationValues[markerType][slide.viewerIndex];
            return newMarkerNormalizationValues;
          }, 'replaceIn');
        });
      }
    },
    [
      viewerSlidesChannelNormalizationSettings,
      slideId,
      normalizationData,
      slide?.channels,
      slide.viewerIndex,
      urlMarkerNormalizationValues,
      defaultFromChannelHistogram,
    ]
  );

  useApplySlideChannelNormalizationSettings({
    slide,
    currentNormalizationId,
    normalizationData,
    isLoadingNormalizationData,
    urlMarkerNormalizationValues,
    shouldAllowViewerUrlFallback,
    defaultFromChannelHistogram,
    refreshExistingChannelSettings: true,
  });
  const synchronizedIsLoadingNormalizationData = useSyncSlideChannelNormalizationLoadingState(
    slide,
    isLoadingNormalizationData
  );

  return {
    currentNormalizationId,
    normalizationData,
    normalizationOptions,
    defaultNormalization,
    setCurrentNormalizationId,
    resetChannelNormalizationSettings,
    isLoadingNormalizationOptions,
    isLoadingNormalizationData: synchronizedIsLoadingNormalizationData,
    currentNormalization,
    refetchNormalization,
  };
};
